/*
 * cart-auto-add.js — Moteur d'auto-ajout au panier (adapté MLBP)
 *
 * Concept porté de NADD : réconciliation par état désiré.
 *   À chaque changement de panier → lire le panier → évaluer la règle sur les
 *   lignes MANUELLES uniquement → calculer le cadeau désiré → diff vs lignes
 *   possédées (taguées _auto_rule) → appliquer seulement le delta.
 *
 * Adapté aux primitives MLBP (PAS Dawn) :
 *   - écoute document sur _EVENT_HELPER.triggerCartChange ('action:cart:updateCart')
 *   - écrit via HELPER_UTIL.fetchData (cart/add.js, cart/change.js)
 *   - sections en query ?sections=cart-drawer,cart-count-bubble (convention MLBP)
 *   - délègue TOUT le rendu + l'ouverture du drawer au DrawerCart existant via
 *     un seul dispatch triggerCartChange (cf. add-all-products-to-cart.js:61-79)
 *
 * Garde-fous : idempotent (diff vide = 0 requête), debounce, écritures sérialisées,
 *   triggers comptés sur lignes manuelles seulement, flag de ré-entrance engineWriting.
 */
(function () {
  'use strict';

  const SOURCE = 'cart-auto-add';
  const PROP_KEY = '_auto_rule';
  const DEBOUNCE_MS = 400;

  // --- Parse config injectée par snippets/cart-auto-add-data.liquid ---
  function parseJSON(id, fallback) {
    const el = document.getElementById(id);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return fallback;
    }
  }

  const RULES = parseJSON('cart-auto-add-rules', []);
  const COLLECTIONS = parseJSON('cart-auto-add-collections', {});

  // Early-exit : aucune règle active → le moteur ne fait rien.
  if (!Array.isArray(RULES) || RULES.length === 0) return;

  // --- État interne ---
  let engineWriting = false; // ré-entrance : muet quand on dispatche notre propre event
  let pending = false;       // coalescing : une seule reconcile en attente
  let chain = Promise.resolve(); // sérialisation des écritures
  let debounceTimer = null;

  const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const POST_HEADERS = {
    Accept: 'application/javascript',
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  };

  function sectionsString() {
    return HELPER_UTIL.sectionRenderUtil('cart-drawer').getSectionsString();
  }

  // --- Helpers panier ---
  function isAuto(line) {
    return !!(line.properties && line.properties[PROP_KEY]);
  }

  function getCart() {
    return fetch(routeRoot + 'cart.js', {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());
  }

  // Quantité manuelle (hors lignes auto) selon le type de trigger.
  function manualMetrics(cart) {
    const manual = cart.items.filter((l) => !isAuto(l));
    const autoCents = cart.items
      .filter(isAuto)
      .reduce((s, l) => s + l.final_line_price, 0);
    return {
      manual: manual,
      // total manuel en centimes = total panier - prix des lignes auto
      manualTotalCents: cart.total_price - autoCents,
      itemCount: manual.reduce((s, l) => s + l.quantity, 0),
    };
  }

  // La règle est-elle déclenchée ? (évaluée sur lignes manuelles uniquement)
  function ruleTriggered(rule, m) {
    const qty = rule.thresholdQty || 1;
    switch (rule.triggerType) {
      case 'cart_total':
        return m.manualTotalCents >= rule.thresholdAmount;
      case 'product_qty': {
        if (!rule.triggerProductId) return false;
        const n = m.manual
          .filter((l) => l.product_id === rule.triggerProductId)
          .reduce((s, l) => s + l.quantity, 0);
        return n >= qty;
      }
      case 'collection_qty': {
        const coll = COLLECTIONS[rule.triggerCollectionHandle];
        if (!coll || !Array.isArray(coll.productIds)) return false;
        const ids = coll.productIds;
        const n = m.manual
          .filter((l) => ids.indexOf(l.product_id) !== -1)
          .reduce((s, l) => s + l.quantity, 0);
        return n >= qty;
      }
      case 'item_count':
        return m.itemCount >= qty;
      default:
        return false;
    }
  }

  // Ensemble cadeau désiré : { variantId: qty } (qty max si conflit).
  function computeDesired(cart) {
    const m = manualMetrics(cart);
    const desired = {};
    RULES.forEach((rule) => {
      if (!rule.targetVariantId) return;
      if (ruleTriggered(rule, m)) {
        const vid = rule.targetVariantId;
        const want = Math.max(1, rule.targetQty || 1);
        // qty max si conflit ; on garde aussi le ruleId qui a demandé ce variant
        if (!desired[vid] || want > desired[vid].quantity) {
          desired[vid] = { quantity: want, ruleId: rule.id };
        }
      }
    });
    return desired;
  }

  // Diff entre désiré et lignes auto actuelles → { adds:[{id,quantity}], removes:[lineKey] }
  function computeDiff(cart, desired) {
    const adds = [];
    const removes = [];
    const currentAuto = {}; // variantId -> { key, quantity }
    cart.items.filter(isAuto).forEach((l) => {
      currentAuto[l.variant_id] = { key: l.key, quantity: l.quantity };
    });

    // Manquants ou mauvaise quantité → (re)ajouter
    Object.keys(desired).forEach((vid) => {
      const want = desired[vid].quantity;
      const ruleId = desired[vid].ruleId;
      const have = currentAuto[vid];
      if (!have) {
        adds.push({ id: Number(vid), quantity: want, ruleId: ruleId });
      } else if (have.quantity !== want) {
        removes.push(have.key);
        adds.push({ id: Number(vid), quantity: want, ruleId: ruleId });
      }
    });

    // Lignes auto qui ne sont plus désirées → retirer
    Object.keys(currentAuto).forEach((vid) => {
      if (!(vid in desired)) removes.push(currentAuto[vid].key);
    });

    return { adds: adds, removes: removes };
  }

  // --- Écritures Ajax ---
  function removeLine(key) {
    return new Promise((resolve) => {
      HELPER_UTIL.fetchData({
        method: 'POST',
        config: { headers: POST_HEADERS },
        url: routeRoot + 'cart/change.js',
        data: JSON.stringify({ id: key, quantity: 0 }),
        onsuccess: () => resolve(),
        onerror: () => resolve(),
      });
    });
  }

  function addItems(adds, withSections) {
    const items = adds.map((a) => ({
      id: a.id,
      quantity: a.quantity,
      properties: { [PROP_KEY]: a.ruleId },
    }));
    const url =
      routeRoot +
      'cart/add.js' +
      (withSections ? '?sections=' + sectionsString() : '');
    return new Promise((resolve) => {
      HELPER_UTIL.fetchData({
        method: 'POST',
        config: { headers: POST_HEADERS },
        url: url,
        data: JSON.stringify({ items: items }),
        onsuccess: (data) => resolve(data),
        onerror: () => resolve(null),
      });
    });
  }

  // GET cart/change.js? — on a besoin des sections après un remove-only.
  function removeLineWithSections(key) {
    return new Promise((resolve) => {
      HELPER_UTIL.fetchData({
        method: 'POST',
        config: { headers: POST_HEADERS },
        url: routeRoot + 'cart/change.js?sections=' + sectionsString(),
        data: JSON.stringify({ id: key, quantity: 0 }),
        onsuccess: (data) => resolve(data),
        onerror: () => resolve(null),
      });
    });
  }

  // Délègue rendu + ouverture du drawer au DrawerCart existant (un seul dispatch).
  function renderViaTheme(data) {
    if (!data || !data.sections) return;
    engineWriting = true; // notre propre event → on s'ignore dans le listener
    const el = window.currentActiveElement || document.body;
    HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.triggerCartChange, el, {
      sections: data.sections,
    });
    // Reset après que le listener (synchrone) a tourné.
    setTimeout(function () {
      engineWriting = false;
    }, 0);
  }

  // --- Cœur : une passe de réconciliation ---
  function reconcileOnce() {
    return getCart().then(function (cart) {
      const desired = computeDesired(cart);
      const diff = computeDiff(cart, desired);

      // Diff vide → NO-OP, zéro écriture (le vrai terminateur de boucle).
      if (diff.adds.length === 0 && diff.removes.length === 0) return;

      let p = Promise.resolve(null);

      // Removes d'abord, séquentiels. Sections sur la DERNIÈRE écriture du batch.
      const lastIsAdd = diff.adds.length > 0;
      diff.removes.forEach(function (key, i) {
        const isLastRemove = !lastIsAdd && i === diff.removes.length - 1;
        p = p.then(function () {
          return isLastRemove ? removeLineWithSections(key) : removeLine(key);
        });
      });

      // Puis un seul add batché (avec sections car c'est la dernière écriture).
      if (lastIsAdd) {
        p = p.then(function () {
          return addItems(diff.adds, true);
        });
      }

      return p.then(function (data) {
        renderViaTheme(data);
      });
    });
  }

  function scheduleReconcile() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      if (pending) return; // déjà une passe en file → elle verra l'état final
      pending = true;
      chain = chain
        .then(function () {
          return reconcileOnce();
        })
        .catch(function () {})
        .then(function () {
          pending = false;
        });
    }, DEBOUNCE_MS);
  }

  // --- Listener : ignore nos propres mutations ---
  function onCartChange() {
    if (engineWriting) return; // ré-entrance : ne pas réagir à notre dispatch
    scheduleReconcile();
  }

  function init() {
    if (typeof HELPER_UTIL === 'undefined' || typeof _EVENT_HELPER === 'undefined') {
      return; // base.js absent → rien à faire
    }
    document.addEventListener(_EVENT_HELPER.triggerCartChange, onCartChange);
    // Passe initiale idempotente (applique la règle au reload si déjà satisfaite).
    scheduleReconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
